import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "../../lib/utils";

const fontSizeClasses = {
  sm: "text-5xl sm:text-6xl md:text-7xl",
  md: "text-6xl sm:text-7xl md:text-8xl",
  lg: "text-7xl sm:text-8xl md:text-9xl",
  xl: "text-8xl sm:text-9xl md:text-[10rem]",
  "2xl": "text-9xl sm:text-[10rem] md:text-[11rem]",
  "3xl": "text-[10rem] sm:text-[11rem] md:text-[12rem]",
};

/**
 * Outline-text horizontal marquee (CRA + Tailwind). Dark footer: use outline="bright".
 */
export const Marquee = React.forwardRef(function Marquee(
  {
    className,
    text,
    repeat = 8,
    duration = 20,
    fontSize = "lg",
    strokeWidth = "1px",
    outline = "bright",
    ...props
  },
  ref,
) {
  const strokeColor =
    outline === "dim" ? "rgb(82 82 82)" : "rgb(148 163 184)";
  const reduceMotion = useReducedMotion();

  const labelClass = cn(
    fontSizeClasses[fontSize] ?? fontSizeClasses.lg,
    "px-4 font-bold text-transparent",
  );
  const outlineStyle = {
    WebkitTextStroke: `${strokeWidth} ${strokeColor}`,
  };

  const fade = (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[15%] bg-gradient-to-r from-black to-transparent md:w-[20%]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[15%] bg-gradient-to-l from-black to-transparent md:w-[20%]"
      />
    </>
  );

  return (
    <div
      ref={ref}
      className={cn("relative w-full overflow-hidden py-12 md:py-16", className)}
      {...props}
    >
      {fade}
      {reduceMotion ? (
        <div className="flex justify-center px-6">
          <span className={labelClass} style={outlineStyle}>
            {text}
          </span>
        </div>
      ) : (
        <motion.div
          className="flex whitespace-nowrap"
          animate={{ x: ["0%", "-50%"] }}
          transition={{
            repeat: Number.POSITIVE_INFINITY,
            ease: "linear",
            duration,
          }}
        >
          {[...Array(repeat)].map((_, index) => (
            <div key={index} className="mx-4 flex items-center md:mx-6">
              <span className={labelClass} style={outlineStyle}>
                {text}
              </span>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
});

Marquee.displayName = "Marquee";

export default Marquee;
